import useMutation from '@hooks/useGQLMutation'
import { useModalReload } from '@hooks/useModalReload'
import { useAlertsStore } from '@stores/alertsStore'
import { SkipSupporterDocument } from '@frontend/graphql/mutations/skipSupporter.generated'
import { UnskipSupporterDocument } from '@frontend/graphql/mutations/unskipSupporter.generated'
import { scopedTranslation } from '@frontend/utils/I18n'
import { toast } from '@stores/toastStore'

const t = scopedTranslation('views.calling_sessions.view_all_supporters')

type UseSkipSupporterOptions = {
  alertsPath: string
  reloadProps?: string[]
}

type SkipOptions = {
  callingSessionSupporterId: string
  supporterName: string
  skipType: string
  expiresAt?: string
  reason?: string
}

type UnskipOptions = {
  callingSessionSupporterId: string
  supporterName: string
}

export function useSkipSupporter({
  alertsPath,
  reloadProps = ['callingSession', 'callingSessionSupporter'],
}: UseSkipSupporterOptions) {
  const { setAlerts } = useAlertsStore(({ setAlerts }) => ({ setAlerts }))
  const reload = useModalReload()
  const skipMutation = useMutation(SkipSupporterDocument)
  const unskipMutation = useMutation(UnskipSupporterDocument)

  const skip = async ({ callingSessionSupporterId, supporterName, skipType, expiresAt, reason }: SkipOptions) => {
    try {
      const response = await skipMutation.mutateAsync({
        callingSessionSupporterId,
        skipType,
        expiresAt,
        reason,
      })

      if (response?.skipSupporter?.success) {
        toast.success(t('alerts.skip_success', { name: supporterName }))
        reload({ only: reloadProps })
        return true
      } else {
        const errors = response?.skipSupporter?.errors || []
        setAlerts({
          alertsPath,
          alerts: errors.map((error: { message: string }) => ({
            type: 'error' as const,
            details: { title: error.message },
          })),
        })
        return false
      }
    } catch (error) {
      console.error('Skip supporter mutation failed:', error)
      setAlerts({
        alertsPath,
        alerts: [
          {
            type: 'error',
            details: { title: t('alerts.skip_error') },
          },
        ],
      })
      return false
    }
  }

  const unskip = async ({ callingSessionSupporterId, supporterName }: UnskipOptions) => {
    try {
      const response = await unskipMutation.mutateAsync({ callingSessionSupporterId })

      if (response?.unskipSupporter?.success) {
        toast.success(t('alerts.unskip_success', { name: supporterName }))
        reload({ only: reloadProps })
        return true
      } else {
        const errors = response?.unskipSupporter?.errors || []
        setAlerts({
          alertsPath,
          alerts: errors.map((error: { message: string }) => ({
            type: 'error' as const,
            details: { title: error.message },
          })),
        })
        return false
      }
    } catch (error) {
      console.error('Unskip supporter mutation failed:', error)
      setAlerts({
        alertsPath,
        alerts: [
          {
            type: 'error',
            details: { title: t('alerts.unskip_error') },
          },
        ],
      })
      return false
    }
  }

  return {
    skip,
    unskip,
    isLoading: skipMutation.isLoading || unskipMutation.isLoading,
  }
}
